/* eslint-disable */
import {
  Type,
  TypeC,
  Props,
  Errors,
  ValidationError,
  getFunctionName,
  identity,
} from "io-ts";
import { isRight, Left, map as mapRight } from "fp-ts/lib/Either";
import { isArray, initial, last, flatten, get } from "lodash";

function stringify(v: any): string {
  if (typeof v === "function") {
    return getFunctionName(v);
  }
  if (typeof v === "number" && !isFinite(v)) {
    if (isNaN(v)) {
      return "NaN";
    }
    return v > 0 ? "Infinity" : "-Infinity";
  }
  return JSON.stringify(v, null, 2);
}

function process(error: ValidationError) {
  const context = error.context;

  const path = flatten([
    initial(context).map((entry) => entry.key),
    get(last(context), "key") + ":" + get(last(context), "type.name") || "NONE",
  ]).join("/");

  return {
    value: stringify(error.value),
    path: path,
  };
}

function report(result: Left<Errors>) {
  return result.left.map((error) => {
    return {
      ...(!!error.message ? { message: error.message } : process(error)),
      object: (get(error.context[0], "actual") as { [k: string]: any }) || {},
    };
  });
}

// Easy type guards!
export function hasProperty(
  key: string,
  input: unknown
): input is { [s in typeof key]: unknown } {
  return (
    typeof input === "object" && input !== null && input.hasOwnProperty(key)
  );
}

export function withDecoded<A, O, OUT>(
  codec: Type<A, O, any>,
  data: any,
  transform: (decoded: A) => OUT
): OUT {
  const decoded = codec.decode(data);

  if (isRight(decoded)) {
    const r = decoded.right;

    return transform(r);
  } else {
    const r = report(decoded);
    console.error(
      `Unable to parse model using ${codec.name}, there were ${decoded.left.length} errors`,
      r
    );
    throw new Error(
      `Unable to parse model using ${codec.name}, there were ${decoded.left.length} errors\n${r}`
    );
  }
}

export function createPresentedCodec<
  P extends Props,
  Extension extends {},
  Out = { [K in keyof P & Extension]: (P & Extension)[K]["_A"] }
>(
  innerCodec: TypeC<P>,
  func: (inner: { [K in keyof P]: P[K]["_A"] }, input: unknown) => Out,
  name: string
) {
  return new Type<Out>(
    name,
    (input: unknown): input is Out => innerCodec.is(input),
    (input: unknown) => {
      return mapRight((model: { [K in keyof P]: P[K]["_A"] }) =>
        func(model, input)
      )(innerCodec.decode(input));
    },
    identity
  );
}

export function flattenData(user: any): any {
  if (user.data) {
    user.data = !isArray(user.data) ? flatten([user.data]) : user.data;
  }
  return user;
}
